import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { paragon } from '@useparagon/connect';

import type { StartConnectionResponse } from '@polyai/schemas';

import { useIsFeatureOn } from 'hooks/useFeatureSwitch/useIsFeatureOn';
import { useIsDemoAccount } from 'hooks/useIsDemoAccount';
import useProjectRoute from 'hooks/useProjectRoute';
import { useStartIntegrationConnection } from 'hooks/useStartIntegrationConnection';
import { useToast } from 'hooks/useToast';

interface ParagonContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  isAvailable: boolean;
  reauthenticate: () => Promise<boolean>;
}

const ParagonContext = createContext<ParagonContextValue>({
  isAuthenticated: false,
  isLoading: true,
  isAvailable: false,
  reauthenticate: async () => false,
});

export const useParagonAuth = () => useContext(ParagonContext);

interface ParagonProviderProps {
  children: ReactNode;
  paragonProjectId?: string;
}

/**
 * Paragon SDK Provider
 *
 * Initializes the Paragon Connect SDK for the current user.
 * Uses the standard Paragon Connect Portal UI for authentication.
 *
 * Setup:
 * 1. Backend provides signed JWT token via /integrations/start-connection endpoint
 * 2. ParagonUserId is constructed as: accountId_projectId
 * 3. Standard Paragon UI is used for OAuth flows
 *
 * @see https://docs.useparagon.com/getting-started/displaying-the-connect-portal
 */
export const ParagonProvider = ({ children }: ParagonProviderProps) => {
  const { isFeatureOn, isLoadingFeatures } = useIsFeatureOn();
  const { isDemoAccount } = useIsDemoAccount();
  // Don't load Paragon SDK for demo accounts since all integrations are restricted
  const isParagonIntegrationsEnabled =
    isDemoAccount || isFeatureOn('paragon_integrations');

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAvailable, setIsAvailable] = useState(false);
  const hasInitialized = useRef(false);
  const queryClient = useQueryClient();
  const { accountId, projectId } = useProjectRoute();
  const toast = useToast();

  // React Query for getting the JWT token - skip if feature flag is off
  const {
    data: authData,
    isSuccess,
    isError,
    refetch,
  } = useStartIntegrationConnection({
    enabled: isParagonIntegrationsEnabled,
  }) as {
    data: StartConnectionResponse | undefined;
    isSuccess: boolean;
    isError: boolean;
    refetch: () => Promise<any>;
  };

  // Re-authenticate function that can be called when auth fails
  const reauthenticate = useCallback(async (): Promise<boolean> => {
    try {
      setIsLoading(true);

      // Invalidate the cached token and fetch a new one
      await queryClient.invalidateQueries([
        'integrations',
        'start-connection',
        accountId,
        projectId,
      ]);
      const { data: newAuthData } = await refetch();

      if (!newAuthData?.signedToken || !newAuthData?.paragonProjectId) {
        throw new Error('Failed to get authentication token');
      }

      // Re-authenticate with Paragon
      await paragon.authenticate(
        newAuthData.paragonProjectId,
        newAuthData.signedToken,
      );
      paragon.setHeadless(true);

      setIsAuthenticated(true);
      setIsAvailable(true);
      hasInitialized.current = true;
      return true;
    } catch (error) {
      console.error('Failed to re-authenticate with Paragon:', error);
      toast.error({
        title: 'Authentication Error',
        description:
          'Failed to refresh authentication. Please refresh the page.',
      });
      setIsAuthenticated(false);
      setIsAvailable(false);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [accountId, projectId, queryClient, refetch, toast]);

  useEffect(() => {
    // Wait for feature flags to finish loading before making any decisions
    if (isLoadingFeatures) {
      return;
    }

    // If feature flag is off, mark as unavailable immediately
    if (!isParagonIntegrationsEnabled) {
      console.log(
        'Paragon integrations feature flag is off - Paragon unavailable.',
      );
      setIsAvailable(false);
      setIsAuthenticated(false);
      setIsLoading(false);
      hasInitialized.current = true;
      return;
    }

    // If the auth request failed, mark as unavailable and stop loading
    if (isError) {
      console.log(
        'Paragon authentication request failed - Paragon unavailable',
      );
      setIsAvailable(false);
      setIsAuthenticated(false);
      setIsLoading(false);
      hasInitialized.current = true;
      return;
    }

    // Skip if already initialized or data not ready
    if (hasInitialized.current || !isSuccess || !authData) {
      return;
    }

    const initializeParagon = async () => {
      try {
        if (!authData.signedToken || !authData.paragonProjectId) {
          throw new Error('Invalid authentication data received');
        }

        await paragon.authenticate(
          authData.paragonProjectId,
          authData.signedToken,
        );

        // Set headless mode globally for all Paragon operations
        paragon.setHeadless(true);

        console.log(
          'Paragon SDK authenticated for user:',
          authData.paragonUserId,
          '(headless mode enabled)',
        );
        setIsAuthenticated(true);
        setIsAvailable(true);
        hasInitialized.current = true;
      } catch (error) {
        console.error('Failed to initialize Paragon SDK:', error);
        setIsAuthenticated(false);
        setIsAvailable(false);
        // Don't throw - allow the UI to render but integrations won't work
        // User will see an error when they try to connect
      } finally {
        setIsLoading(false);
      }
    };

    void initializeParagon();
  }, [
    isLoadingFeatures,
    isParagonIntegrationsEnabled,
    isSuccess,
    isError,
    authData,
  ]);

  return (
    <ParagonContext.Provider
      value={{ isAuthenticated, isLoading, isAvailable, reauthenticate }}
    >
      {children}
    </ParagonContext.Provider>
  );
};
